import { ProductMapper } from "@omni/mappers/product/product.mapper";
import { ProductsMapper } from "@omni/mappers/product/products.mapper";
import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { AlgoliaRecommendParams } from "@omni/types/api/search/algolia/recommend/algoliaRecommendParams.model";
import { AlgoliaRecommendZone } from "@omni/types/api/search/algolia/shared/enum/algoliaRecommendZone.enum";
import { BffSearchProduct } from "@omni/types/api/search/bff/product/product.model";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty } from "lodash-es";
import { useCallback, useEffect, useState } from "react";

import { useBFFRecommendServiceApi } from "@/api/hooks/recommendService.hook";
import { ProductSliderSkeletonComponent } from "@/components/Product/ProductSlider/components/skeleton/productSliderSkeleton.component";
import { ProductSliderSourceContextProvider } from "@/components/Product/ProductSlider/context/productSliderSource.context";
import { filterVisible } from "@/components/Product/ProductSlider/productSlider.helpers";
import { getCartSkuList } from "@/components/Product/ProductSlider/sources/algoliaRecommend/alogliaRecommend.helpers";
import { useProductCartContext } from "@/utils/hooks/cart.hook";

type AlgoliaRecommendProductsliderProps = {
    algoliaRecommendParams: AlgoliaRecommendParams;
    sku?: string;
    serverSideProducts?: Array<Product<ProductFieldSet.ProductTile>>;
};

export const AlgoliaRecommendProductslider = ({
    sku,
    algoliaRecommendParams,
    serverSideProducts,
    children
}: RequireChildrenFor<AlgoliaRecommendProductsliderProps>) => {
    const { cart } = useProductCartContext();
    const cartSkuList = getCartSkuList(cart);

    const [products, setProducts] = useState(serverSideProducts);

    const [{ loading, error }, loadRecommendations] = useBFFRecommendServiceApi<
        any,
        Array<BffSearchProduct>,
        Array<Product<ProductFieldSet.ProductTile>>
    >(
        {
            url: `/recommend/frequentlyBoughtTogether`,
            method: "POST",
            mapper: ProductsMapper.fromList(
                ProductMapper.fromBffSearchToProductTile
            )
        },
        {
            manual: true,
            useCache: false,
            autoCancel: false
        }
    );

    const updateRecommendations = useCallback(
        (skus: Array<string>) => {
            if (isEmpty(skus)) {
                setProducts([]);
            } else {
                loadRecommendations({
                    data: {
                        sku: skus.join(","),
                        algoliaRecommendClientConfig: {
                            indexName: algoliaRecommendParams.indexName,
                            threshold: algoliaRecommendParams.threshold,
                            maxRecommendations: algoliaRecommendParams.max
                        }
                    }
                })
                    .then(({ data }) => data ?? [])
                    .then(filterVisible)
                    .then(setProducts);
            }
        },
        [
            algoliaRecommendParams.indexName,
            algoliaRecommendParams.max,
            algoliaRecommendParams.threshold,
            loadRecommendations
        ]
    );

    useEffect(() => {
        if (isDefined(products)) return;
        if (loading || error) return;

        if (algoliaRecommendParams.zone === AlgoliaRecommendZone.ShoppingCart) {
            updateRecommendations(cartSkuList);
        } else if (sku) {
            updateRecommendations([sku]);
        }
    }, [
        cartSkuList,
        sku,
        products,
        updateRecommendations,
        algoliaRecommendParams.zone,
        error,
        loading
    ]);

    if (error) {
        return null;
    } else if (isDefined(products) && isEmpty(products)) {
        return null;
    } else if (isDefined(products) && !isEmpty(products)) {
        return (
            <ProductSliderSourceContextProvider products={products}>
                {children}
            </ProductSliderSourceContextProvider>
        );
    } else {
        return <ProductSliderSkeletonComponent />;
    }
};
